import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app, db } from "../utils/firebaseConfig.js";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../Components/AdminHeader.js";
import useFirestoreCollection from "../utils/useFirestoreCollection.js";
import "../Styles/Admin.css";
import Switch from "react-switch";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import WorkingHours from "../Components/WorkingHours.js";
import UpdateWorkingHours from "../Components/UpdateWorkingHours.js";
import ManagePhotos from "../Components/ManagePhotos.js";
import ManageSignatureDishes from "../Components/ManageSignatureDishes.js";
import ManageReservations from "../Components/ManageReservations.js";
import { FaPencilAlt, FaChevronDown, FaBell } from "react-icons/fa";
import {
  enableAdminPush,
  pushSupported,
  currentPushPermission,
  listenForegroundPush,
} from "../utils/push.js";

const AdminPage = () => {
  // Swap the PWA manifest while the admin page is open, so the
  // "Install app" prompt installs the Admin PWA (not the public site).
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const original = link.getAttribute("href");
    link.setAttribute("href", "/admin.webmanifest");
    return () => {
      if (original !== null) link.setAttribute("href", original);
    };
  }, []);

  const [editWorkingHours, setEditWorkingHours] = useState(false);
  const [newsLines, setNewsLines] = useState(["", "", ""]);
  const [user, setUser] = useState(null);
  const [isWinter, setIsWinter] = useState(true);
  const [showPhotos, setShowPhotos] = useState(false);
  const [showDishes, setShowDishes] = useState(false);
  // Push notifications: "off" | "on" | "denied" | "unsupported" | "busy" | "error"
  const [pushState, setPushState] = useState("off");
  const [closingDate, setClosingDate] = useState(new Date());
  const [editClosingDate, setEditClosingDate] = useState(false);
  const [closingDraft, setClosingDraft] = useState(new Date());
  const [bookings, setBookings] = useState(false);
  const { data: workingHoursData } = useFirestoreCollection("hours");
  const {
    loading: adminLoading,
    error: adminError,
    data: adminData,
  } = useFirestoreCollection("administration"); //change to administration for production or testAdmin for testing

  useEffect(() => {
    if (adminData && adminData.length > 0) {
      setIsWinter(adminData[0].isWinter);
      const rawNews = adminData[0].news;
      const newsArr = Array.isArray(rawNews)
        ? rawNews
        : rawNews
        ? [rawNews]
        : [];
      setNewsLines([newsArr[0] || "", newsArr[1] || "", newsArr[2] || ""]);
      setClosingDate(
        adminData[0].closingDate?.toDate
          ? adminData[0].closingDate.toDate()
          : new Date()
      );
      setBookings(adminData[0].bookings);
    }
  }, [adminData]);

  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is authenticated, set the user state
        setUser(user);
      } else {
        // User is not authenticated, redirect to the login page or handle as needed

        // You can use React Router or any other method to navigate to the login page
        setUser(null);
        navigate("/login");
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  // Determine the current notification state for this device, and surface
  // foreground pushes (when the dashboard is already open) as a quick alert.
  useEffect(() => {
    let active = true;
    (async () => {
      const supported = await pushSupported();
      if (!active) return;
      if (!supported) {
        setPushState("unsupported");
        return;
      }
      setPushState(currentPushPermission());
    })();
    listenForegroundPush((payload) => {
      const d = (payload && payload.data) || {};
      window.alert(d.title || "Νέο αίτημα κράτησης");
    });
    return () => {
      active = false;
    };
  }, []);

  const handleEditWorkingHoursToggle = () => {
    setEditWorkingHours((prevEditWorkingHours) => !prevEditWorkingHours);
  };

  const handleEnableBookings = async () => {
    const confirmed = window.confirm("Είστε σίγουροι;");
    if (confirmed) {
      try {
        const docRef = doc(db, "administration", adminData[0].id); // dynamic: uses the loaded doc's own ID, so it works in both testAdmin and administration
        await updateDoc(docRef, {
          bookings: !bookings,
        });
        setBookings(!bookings);
      } catch (error) {
        console.log("Error toggling bookings:", error);
      }
    }
  };

  const handleNewsLineChange = (index, value) => {
    setNewsLines((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleNewsUpdate = async () => {
    try {
      const docRef = doc(db, "administration", adminData[0].id); // dynamic: uses the loaded doc's own ID, so it works in both testAdmin and administration
      const cleaned = newsLines
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 3);
      await updateDoc(docRef, {
        news: cleaned, // stored as an array of up to 3 phrases
      });
      setNewsLines([cleaned[0] || "", cleaned[1] || "", cleaned[2] || ""]);
    } catch (error) {
      console.log("Error updating news:", error);
    }
  };

  const handleSeasonalStatusToggle = async () => {
    const confirmed = window.confirm("Είστε σίγουροι;");
    if (confirmed) {
      try {
        const docRef = doc(db, "administration", adminData[0].id); // dynamic: uses the loaded doc's own ID, so it works in both testAdmin and administration
        await updateDoc(docRef, {
          isWinter: !isWinter,
        });
        setIsWinter(!isWinter);
      } catch (error) {
        console.log("Error toggling seasonal status:", error);
      }
    }
  };

  const handleClosingDateUpdate = async () => {
    if (!(closingDraft instanceof Date) || isNaN(closingDraft)) {
      window.alert("Παρακαλώ επιλέξτε έγκυρη ημερομηνία.");
      return;
    }
    const confirmed = window.confirm("Είστε σίγουροι;");
    if (!confirmed) return;
    try {
      const docRef = doc(db, "administration", adminData[0].id); // dynamic: uses the loaded doc's own ID, so it works in both testAdmin and administration
      await updateDoc(docRef, {
        closingDate: closingDraft, // a JS Date is stored as a Firestore Timestamp
      });
      setClosingDate(closingDraft);
      setEditClosingDate(false);
    } catch (error) {
      console.log("Error updating closing date:", error);
    }
  };

  const handleEnablePush = async () => {
    setPushState("busy");
    try {
      await enableAdminPush(user && user.uid);
      setPushState("on");
    } catch (err) {
      const code = err && err.message;
      if (code === "denied") setPushState("denied");
      else if (code === "unsupported") setPushState("unsupported");
      else setPushState("error");
      console.log("Error enabling push:", code);
    }
  };

  if (!user) {
    return <div>WARNING!!! You are not authenticated.</div>;
  }

  return (
    <div className="admin_page">
      <AdminHeader />

      <div className="admin_top_section">
        {adminLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="admin_card functioning">
            <h3>ΛΕΙΤΟΥΡΓΙΑ</h3>

            <div className="functioning__line">
              <h4 data-open={isWinter ? "false" : "true"}>
                {isWinter ? "ΧΕΙΜΩΝΑΣ — ΚΛΕΙΣΤΑ" : "ΚΑΛΟΚΑΙΡΙ — ΑΝΟΙΚΤΑ"}
              </h4>
              <Switch
                checked={!isWinter}
                onChange={handleSeasonalStatusToggle}
                offColor="#c62828"
                onColor="#2e7d32"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={48}
              />
            </div>

            <div className="functioning__line">
              <h4 data-open={bookings ? "true" : "false"}>
                {bookings
                  ? "ΚΡΑΤΗΣΕΙΣ ΕΝΕΡΓΕΣ"
                  : "ΚΡΑΤΗΣΕΙΣ ΚΛΕΙΣΤΕΣ"}
              </h4>
              <Switch
                checked={bookings}
                onChange={handleEnableBookings}
                offColor="#c62828"
                onColor="#2e7d32"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={48}
              />
            </div>

            {bookings && (
              <div className="closing_date">
                <h4>ΚΡΑΤΗΣΕΙΣ ΜΕΧΡΙ:</h4>
                {editClosingDate ? (
                  <div className="closing_date__edit">
                    <DatePicker
                      selected={closingDraft}
                      onChange={(d) => setClosingDraft(d)}
                      showTimeSelect
                      dateFormat="d MMMM, yyyy HH:mm"
                      timeFormat="HH:mm"
                      minDate={new Date()}
                      timeIntervals={30}
                      shouldCloseOnSelect={true}
                      popperPlacement="bottom-start"
                      popperProps={{ strategy: "fixed" }}
                      portalId="datepicker-portal"
                    />
                    <div className="closing_date__actions">
                      <button
                        onClick={handleClosingDateUpdate}
                        className="edit-button"
                      >
                        <FaPencilAlt />
                        <p>ΑΠΟΘΗΚΕΥΣΗ</p>
                      </button>
                      <button
                        onClick={() => setEditClosingDate(false)}
                        className="edit-button"
                      >
                        <p>ΑΚΥΡΩΣΗ</p>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="closing_date__view">
                    <p>{closingDate.toLocaleString()}</p>
                    <button
                      onClick={() => {
                        setClosingDraft(closingDate);
                        setEditClosingDate(true);
                      }}
                      className="edit-button"
                    >
                      <FaPencilAlt />
                      <p>ΕΠΕΞΕΡΓΑΣΙΑ</p>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="admin_card working-hours">
          <h3>ΩΡΑΡΙΟ</h3>
          {editWorkingHours ? (
            <UpdateWorkingHours
              handleEditWorkingHoursToggle={handleEditWorkingHoursToggle}
            />
          ) : (
            <>
              <WorkingHours />
              <button
                onClick={handleEditWorkingHoursToggle}
                className="edit-button"
              >
                <FaPencilAlt />
                <p>ΕΠΕΞΕΡΓΑΣΙΑ</p>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="admin_card notifications">
        <h3>ΕΙΔΟΠΟΙΗΣΕΙΣ</h3>
        {pushState === "on" ? (
          <p className="notifications__status is-on">
            <FaBell /> Οι ειδοποιήσεις είναι ενεργές σε αυτή τη συσκευή.
          </p>
        ) : pushState === "denied" ? (
          <p className="notifications__status is-off">
            Οι ειδοποιήσεις είναι αποκλεισμένες. Επίτρεψέ τες από τις ρυθμίσεις
            του browser για αυτόν τον ιστότοπο και δοκίμασε ξανά.
          </p>
        ) : pushState === "unsupported" ? (
          <p className="notifications__status is-off">
            Αυτή η συσκευή/browser δεν υποστηρίζει ειδοποιήσεις. Στο κινητό,
            πρόσθεσε πρώτα την εφαρμογή στην αρχική οθόνη («Προσθήκη στην αρχική»)
            και άνοιξέ την από εκεί.
          </p>
        ) : (
          <>
            <p className="notifications__hint">
              Λάβε ειδοποίηση στο κινητό μόλις υποβληθεί νέο αίτημα κράτησης.
            </p>
            <button
              onClick={handleEnablePush}
              className="edit-button"
              disabled={pushState === "busy"}
            >
              <FaBell />
              <p>
                {pushState === "busy"
                  ? "ΕΝΕΡΓΟΠΟΙΗΣΗ…"
                  : pushState === "error"
                  ? "ΔΟΚΙΜΑΣΕ ΞΑΝΑ"
                  : "ΕΝΕΡΓΟΠΟΙΗΣΗ ΕΙΔΟΠΟΙΗΣΕΩΝ"}
              </p>
            </button>
          </>
        )}
      </div>

      <div className="admin_card reservations">
        <h3>ΑΙΤΗΜΑΤΑ ΚΡΑΤΗΣΗΣ</h3>
        <ManageReservations />
      </div>

      <div className="admin_card news">
        <h3>ΝΕΑ</h3>
        <p className="news_hint">
          Έως 3 φράσεις. Εμφανίζονται η μία μετά την άλλη.
        </p>
        <div className="news_lines">
          {newsLines.map((line, i) => (
            <div className="news_line" key={i}>
              <span className="news_line__num">{i + 1}</span>
              <input
                type="text"
                value={line}
                placeholder={`Φράση ${i + 1}`}
                maxLength={120}
                onChange={(e) => handleNewsLineChange(i, e.target.value)}
              />
            </div>
          ))}
        </div>
        <button onClick={handleNewsUpdate} className="edit-button">
          <FaPencilAlt />
          <p>ΑΠΟΘΗΚΕΥΣΗ</p>
        </button>
      </div>

      <div className={`admin_card photos${showPhotos ? " is-open" : ""}`}>
        <button
          type="button"
          className="admin_card__toggle"
          aria-expanded={showPhotos}
          onClick={() => setShowPhotos((v) => !v)}
        >
          <h3>ΦΩΤΟΓΡΑΦΙΕΣ</h3>
          <FaChevronDown className="admin_card__chevron" />
        </button>
        {showPhotos && (
          <div className="admin_card__body">
            <ManagePhotos />
          </div>
        )}
      </div>

      <div className={`admin_card dishes${showDishes ? " is-open" : ""}`}>
        <button
          type="button"
          className="admin_card__toggle"
          aria-expanded={showDishes}
          onClick={() => setShowDishes((v) => !v)}
        >
          <h3>ΠΙΑΤΑ-ΣΗΜΑ</h3>
          <FaChevronDown className="admin_card__chevron" />
        </button>
        {showDishes && (
          <div className="admin_card__body">
            <ManageSignatureDishes />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
